import type { DeleteContractImportJobMutationOptions } from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { type UUID } from '~/types/common';

import { VestigasHTTPError } from '@vestigas/data';

import { queryKeysContractImportJob } from '../queryKeys';

import { deleteContractImportJob } from './deleteContractImportJob';

/**
 * Mutation key for deleting a contract import job.
 * Used with `useMutationState` to track pending deletions per job.
 */
export const MUTATION_KEY_DELETE_CONTRACT_IMPORT_JOB = [
  'contractImportJobs',
  'delete',
] as const;

/**
 * Custom hook for soft-deleting a contract import job.
 *
 * @param options - Optional mutation options.
 * @returns An object containing the mutation function and related properties.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Contract-Import-Jobs/operation/delete_import_job_contracts_import_jobs__job_id__delete
 */
export const useMutationDeleteContractImportJob = (
  options?: DeleteContractImportJobMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation<void, VestigasHTTPError, UUID>({
    mutationFn: async (jobId: UUID) => deleteContractImportJob(jobId),
    mutationKey: MUTATION_KEY_DELETE_CONTRACT_IMPORT_JOB,
    ...options,
    onSuccess(data, variables, onMutateResult, context) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysContractImportJob.getAll(),
      });

      return options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
